import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { api, useApi, type SearchResults } from '../api'
import { Icon, PodAv } from './primitives'

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function useSearch(q: string) {
  const term = useDebounced(q.trim(), 200)
  const { data, loading } = useApi<SearchResults>(term ? api.search(term) : null)
  return { term, data, loading: !!term && loading }
}

function Results({ term, data, loading, onPick }: { term: string; data?: SearchResults; loading: boolean; onPick: () => void }) {
  if (!term) return <div className="search-hint">Search tickers, companies and podcasts</div>
  if (loading && !data) return <div className="search-hint">Searching…</div>
  if (!data || (data.stocks.length === 0 && data.podcasts.length === 0)) {
    return <div className="search-hint">No matches for “{term}”</div>
  }
  return (
    <div className="search-results" data-testid="search-results">
      {data.stocks.length > 0 && <div className="search-group">Stocks</div>}
      {data.stocks.map((s) => (
        <Link key={s.ticker} to={`/stocks/${s.ticker}`} className="m-row" onClick={onPick}>
          <div className="meta">
            <span className="a tick">{s.ticker}</span>
            <span className="b">{s.name}</span>
          </div>
          <span className="chev"><Icon name="chevron" size={16} /></span>
        </Link>
      ))}
      {data.podcasts.length > 0 && <div className="search-group">Podcasts</div>}
      {data.podcasts.map((p) => (
        <Link key={p.id} to={`/podcasts/${p.id}`} className="m-row" onClick={onPick}>
          <PodAv name={p.name} image={p.image_url} size="lg sq" />
          <div className="meta"><span className="a">{p.name}</span></div>
          <span className="chev"><Icon name="chevron" size={16} /></span>
        </Link>
      ))}
    </div>
  )
}

// Mobile: a full-screen sheet opened from the top bar's search icon.
export function SearchOverlay({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('')
  const { term, data, loading } = useSearch(q)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  return (
    <div className="search-sheet" role="dialog" aria-label="Search">
      <div className="search-sheet-top">
        <div className="search-field">
          <Icon name="search" size={18} />
          <input
            autoFocus
            type="search"
            inputMode="search"
            enterKeyHint="search"
            placeholder="Search tickers, podcasts…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search"
          />
        </div>
        <button className="search-cancel" onClick={onClose}>Cancel</button>
      </div>
      <div className="search-sheet-body">
        <Results term={term} data={data} loading={loading} onPick={onClose} />
      </div>
    </div>
  )
}

// Desktop: the design's search box in the page top bar, with a dropdown.
export function SearchBox() {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const { term, data, loading } = useSearch(q)

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  return (
    <div className="search-wrap" ref={wrap}>
      <label className="search">
        <Icon name="search" size={14} />
        <input
          type="search"
          placeholder="Search tickers, podcasts…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          aria-label="Search"
        />
      </label>
      {open && term && (
        <div className="search-drop">
          <Results
            term={term}
            data={data}
            loading={loading}
            onPick={() => {
              setOpen(false)
              setQ('')
            }}
          />
        </div>
      )}
    </div>
  )
}
